import { db } from "./firebaseConfig";
import { doc, setDoc, getDoc, deleteDoc, collection, getDocs, runTransaction, serverTimestamp, query, where, orderBy, onSnapshot } from "firebase/firestore";

// Save a movie by ID
export async function saveMovie(movieId, data = {}) {
  await setDoc(doc(db, "movies", String(movieId)), { id: movieId, ...data });
}

// Get a movie by ID
export async function getMovie(movieId) {
  const docSnap = await getDoc(doc(db, "movies", String(movieId)));
  return docSnap.exists() ? docSnap.data() : null;
}

// Delete a movie by ID
export async function deleteMovie(movieId) {
  await deleteDoc(doc(db, "movies", String(movieId)));
}

// Get all movies
export async function getAllMovies() {
  const querySnapshot = await getDocs(collection(db, "movies"));
  return querySnapshot.docs.map(doc => doc.data());
}

// Save a user by ID
export async function saveUser(userId, name, email) {
  // Upsert user profile; doc id is the user's uid
  await setDoc(
    doc(db, "users", String(userId)),
    {
  uid: userId,
      name: name || "",
      email: email || "",
    },
    { merge: true }
  );
}

// Get a user by ID from 'users' collection
export async function getUser(userId) {
  if (!userId) return null;
  const snap = await getDoc(doc(db, "users", String(userId)));
  return snap.exists() ? (snap.data() || null) : null;
}

// --- Book Later helpers ---
// Save or update a movie in the user's book-later list (users/{uid}/bookLater/{movieId})
export async function saveBookLater(userId, movie) {
  if (!userId || !movie || !movie.id) return;
  const minimal = {
    id: movie.id,
    title: movie.title || movie.name || "",
    poster_path: movie.poster_path || "",
    backdrop_path: movie.backdrop_path || "",
    overview: movie.overview || "",
    runtime: movie.runtime || null,
    genres: Array.isArray(movie.genres)
      ? movie.genres.map((g) => (typeof g === "string" ? g : g.name)).filter(Boolean)
      : [],
    vote_average: movie.vote_average ?? null,
    release_date: movie.release_date || null,
    adult: !!movie.adult,
    savedAt: Date.now(),
  };
  await setDoc(doc(db, "users", String(userId), "bookLater", String(movie.id)), minimal, { merge: true });
}

// Remove a movie from the user's book-later list
export async function removeBookLater(userId, movieId) {
  if (!userId || !movieId) return;
  await deleteDoc(doc(db, "users", String(userId), "bookLater", String(movieId)));
}

// Get all "book later" movies for a user
export async function getBookLater(userId) {
  if (!userId) return [];
  const qs = await getDocs(collection(db, "users", String(userId), "bookLater"));
  return qs.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// --- Cinema Halls Scheduling ---
// Save schedule for a specific hall document in collection `cinemaHall` (e.g., Hall1..Hall5)
export async function saveHallSchedule(hallId, schedule) {
  if (!hallId) throw new Error("hallId is required");
  // schedule: Array<{ movieId, title, date, time, showtime }>
  await setDoc(
    doc(db, "cinemaHall", String(hallId)),
    { hall: String(hallId), schedule: Array.isArray(schedule) ? schedule : [] },
    { merge: true }
  );
}

// Get schedule for a hall (returns [] if none)
export async function getHallSchedule(hallId) {
  if (!hallId) return [];
  const snap = await getDoc(doc(db, "cinemaHall", String(hallId)));
  if (!snap.exists()) return [];
  const data = snap.data() || {};
  return Array.isArray(data.schedule) ? data.schedule : [];
}

// Convenience: save schedules for multiple halls at once
export async function saveMultipleHallSchedules(hallSchedules) {
  // hallSchedules: Record<hallId, scheduleArray>
  const entries = Object.entries(hallSchedules || {});
  for (const [hallId, schedule] of entries) {
    // eslint-disable-next-line no-await-in-loop
    await saveHallSchedule(hallId, schedule);
  }
}

// Get all halls with schedules: returns array of { id, hall, schedule }
export async function getAllHallSchedules() {
  const qs = await getDocs(collection(db, "cinemaHall"));
  return qs.docs.map((d) => {
    const data = d.data() || {};
    return {
      id: d.id,
      hall: data.hall || d.id,
      schedule: Array.isArray(data.schedule) ? data.schedule : [],
    };
  });
}

// Read bookedSeats for a schedule entry matched by (hallId, date, time, optional movieId)
export async function getBookedSeatsForSchedule({ hallId, date, time, movieId }) {
  if (!hallId || !date || !time) return [];
  const ref = doc(db, "cinemaHall", String(hallId));
  const snap = await getDoc(ref);
  if (!snap.exists()) return [];
  const data = snap.data() || {};
  const schedule = Array.isArray(data.schedule) ? data.schedule : [];
  const entry = schedule.find((s) =>
    String(s?.date || "") === String(date) &&
    String(s?.time || "") === String(time) &&
    (movieId == null || String(s?.movieId || "") === String(movieId))
  );
  const booked = Array.isArray(entry?.bookedSeats) ? entry.bookedSeats : [];
  return booked.map(String);
}

// Append seats into the matching schedule entry (by date+time, optionally movieId)
// Path shape: cinemaHall/{hallId} with field schedule: Array<{date, time, movieId, ... , bookedSeats?: string[] }>
export async function appendBookedSeatsToSchedule({ hallId, date, time, movieId, seats }) {
  if (!hallId || !date || !time) return;
  const seatList = Array.isArray(seats) ? seats.map(String).filter(Boolean) : [];
  if (seatList.length === 0) return;

  const ref = doc(db, "cinemaHall", String(hallId));

  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) {
      throw new Error(`Hall document not found: ${hallId}`);
    }
    const data = snap.data() || {};
    const schedule = Array.isArray(data.schedule) ? data.schedule : [];

    const idx = schedule.findIndex((s) =>
      String(s?.date || "") === String(date) &&
      String(s?.time || "") === String(time) &&
      (movieId == null || String(s?.movieId || "") === String(movieId))
    );

    if (idx === -1) {
      // Do not create a new entry here to preserve the unique-per-slot rule
      throw new Error(`Showtime not found in schedule for ${hallId} at ${date} ${time}`);
    }

    const curr = schedule[idx] || {};
    const existing = Array.isArray(curr.bookedSeats) ? curr.bookedSeats.map(String) : [];
    const merged = Array.from(new Set([...existing, ...seatList]));

    const updated = { ...curr, bookedSeats: merged };
    const newSchedule = schedule.slice();
    newSchedule[idx] = updated;

    tx.update(ref, { schedule: newSchedule, updatedAt: serverTimestamp() });
  });
}

// Remove seats from the matching schedule entry's bookedSeats array
export async function removeBookedSeatsFromSchedule({ hallId, date, time, movieId, seats }) {
  if (!hallId || !date || !time) return;
  const seatList = Array.isArray(seats) ? seats.map(String).filter(Boolean) : [];
  if (seatList.length === 0) return;

  const ref = doc(db, "cinemaHall", String(hallId));
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) return; // nothing to do
    const data = snap.data() || {};
    const schedule = Array.isArray(data.schedule) ? data.schedule : [];

    const idx = schedule.findIndex((s) =>
      String(s?.date || "") === String(date) &&
      String(s?.time || "") === String(time) &&
      (movieId == null || String(s?.movieId || "") === String(movieId))
    );

    if (idx === -1) return; // not found

    const curr = schedule[idx] || {};
    const existing = Array.isArray(curr.bookedSeats) ? curr.bookedSeats.map(String) : [];
    const removeSet = new Set(seatList);
    const filtered = existing.filter((s) => !removeSet.has(String(s)));

    const updated = { ...curr, bookedSeats: filtered };
    const newSchedule = schedule.slice();
    newSchedule[idx] = updated;
    tx.update(ref, { schedule: newSchedule, updatedAt: serverTimestamp() });
  });
}

// --- Bookings documents ---
// Create or upsert a booking document under collection 'bookings' with reservationId as the doc id
export async function saveBooking({
  reservationId,
  userId,
  userEmail,
  movieId,
  movieTitle,
  hallId,
  date,
  time,
  seats = [],
  totalPrice = null,
}) {
  if (!reservationId) throw new Error("reservationId is required for saveBooking");
  const payload = {
    reservationId: String(reservationId),
    userId: userId != null ? String(userId) : null,
    userEmail: userEmail || null,
    movieId: movieId != null ? String(movieId) : null,
    movieTitle: movieTitle || "",
    hallId: hallId || "",
    date: date || "",
    time: time || "",
    showtime: date && time ? `${date} ${time}` : (time || ""),
    seats: Array.isArray(seats) ? seats.map(String) : [],
    totalPrice: totalPrice != null ? Number(totalPrice) : null,
    createdAt: serverTimestamp(),
  };
  await setDoc(doc(db, "bookings", String(reservationId)), payload, { merge: true });
}

// Query all bookings for a specific user (most recent first)
export async function getBookingsForUser(userId) {
  if (!userId) return [];
  const colRef = collection(db, "bookings");
  const qOrdered = query(colRef, where("userId", "==", String(userId)), orderBy("createdAt", "desc"));
  try {
    const qs = await getDocs(qOrdered);
    return qs.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (err) {
    // Fallback without orderBy (no index needed); sort client-side
    const qSimple = query(colRef, where("userId", "==", String(userId)));
    const qs = await getDocs(qSimple);
    return qs.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (b.createdAt?.toMillis?.() ?? 0) - (a.createdAt?.toMillis?.() ?? 0));
  }
}

// Optional: real-time subscription to user bookings
export function subscribeBookingsForUser(userId, callback) {
  if (!userId) return () => {};
  const colRef = collection(db, "bookings");
  const qOrdered = query(colRef, where("userId", "==", String(userId)), orderBy("createdAt", "desc"));

  let activeUnsub = onSnapshot(
    qOrdered,
    (snap) => {
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      callback(items);
    },
    (error) => {
      // Fallback: subscribe without orderBy, sort client-side
      if (activeUnsub) activeUnsub();
      const qSimple = query(colRef, where("userId", "==", String(userId)));
      activeUnsub = onSnapshot(qSimple, (snap2) => {
        const items = snap2.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (b.createdAt?.toMillis?.() ?? 0) - (a.createdAt?.toMillis?.() ?? 0));
        callback(items);
      });
    }
  );

  return () => {
    if (activeUnsub) activeUnsub();
  };
}

// Delete a booking document by reservationId
export async function deleteBooking(reservationId) {
  if (!reservationId) return;
  await deleteDoc(doc(db, "bookings", String(reservationId)));
}